import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Steps, Tag, Timeline, Tooltip, Typography } from 'antd';
import DualESignatureAuth from '@/components/DualESignatureAuth';
import Axios from 'axios';
import { toast } from 'react-toastify';

const { Text } = Typography;
const bp = '/bp';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });

const STATUS_TAG = { DRAFT: 'default', UNDER_REVIEW: 'processing', APPROVED: 'success', REJECTED: 'error', RETURNED: 'warning' };
const STEP_STATUS = { Approved: 'finish', Pending: 'process', Rejected: 'error', Skipped: 'wait', Returned: 'error' };

/**
 * Generic approval chain panel (W1–W7): derived status, stage stepper, submit / approve / return /
 * reject with e-signature (password) when the stage requires it, and the action history.
 * objectType: Product | MFR | Recipe | LibraryProcess
 */
export default function ApprovalPanel({ objectType, objectId, compact = false, actionsOnly = false, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // {action, step}
  const [comment, setComment] = useState('');
  const [ccRef, setCcRef] = useState('');

  const load = useCallback(async () => {
    if (!objectId) return;
    try {
      const res = await Axios.get(`${bp}/approval/status/`, { params: { object_type: objectType, object_id: objectId }, ...auth() });
      setData(res?.data?.data || null);
    } catch (e) { setData(null); }
  }, [objectType, objectId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await Axios.post(`${bp}/approval/submit/`, { object_type: objectType, object_id: objectId, change_control_ref: ccRef }, auth());
      toast.success(res?.data?.message || 'Submitted');
      setCcRef(''); await load(); onChanged?.();
    } catch (e) { toast.warning(e?.response?.data?.message || 'Submit failed'); }
    setBusy(false);
  };

  // 2026-10-12 · a stage that needs an e-signature is signed through the app's reusable e-signature
  // module (DualESignatureAuth: "Password Auth" or "21 CFR Part 11" digital certificate). That module
  // creates the signature record; its id travels with the decision and the server attaches it only
  // if it is the actor's own, for this record, recent and unused. `signed` carries {comment,
  // signature_id}; a stage without e-signature acts from the plain dialog below.
  const act = async (signed) => {
    if (!dialog) return;
    setBusy(true);
    try {
      const body = signed ? { action: dialog.action, comment: signed.comment, signature_id: signed.signature_id }
                          : { action: dialog.action, comment };
      const res = await Axios.post(`${bp}/approval/act/${dialog.step.id}/`, body, auth());
      toast.success(res?.data?.message || 'Recorded');
      setDialog(null); setComment(''); await load(); onChanged?.();
    } catch (e) { toast.warning(e?.response?.data?.message || 'Action failed'); }
    setBusy(false);
  };

  if (!data) return null;
  const openStep = (data.steps || []).find((s) => s.status === 'Pending');
  const notMine = data.can_act === false;
  const canSubmit = data.workflow_configured && ['DRAFT', 'REJECTED', 'RETURNED'].includes(data.status);

  // 2026-09-01, "make it single line, design better" — compact mode (the MFR popup's Descriptions
  // row, and now the MFR Approval queue's expanded row) used to stack status / stepper / buttons
  // across three separate blocks — tall for something meant to be a compact inline summary. The
  // Steps stepper duplicates what `position` already says in words, so compact mode drops it and
  // puts status, stage, e-sign note and the actions all in one wrapping row instead; the full
  // stepper (useful for a multi-stage chain's whole shape) stays in non-compact mode.
  const statusLine = (
    <>
      <Tag color={STATUS_TAG[data.status] || 'default'}>{data.status.replace('_', ' ')}</Tag>
      <Text type="secondary">{data.position}</Text>
      {data.workflow_name && <Text type="secondary">· {data.workflow_name}</Text>}
      {data.object_version && <Tag>{data.object_version}</Tag>}
    </>
  );
  const actions = (
    <>
      {canSubmit && (
        <>
          <Input size="small" placeholder="Change-control ref (optional)" value={ccRef} onChange={(e) => setCcRef(e.target.value)} style={{ width: 200 }} />
          <Button size="small" type="primary" loading={busy} onClick={submit}>Submit for approval</Button>
        </>
      )}
      {openStep && (
        <>
          {openStep.esign_required && <Tag color="gold">e-sign required</Tag>}
          {/* 2026-10-07 · only the person whose turn it is may sign: for anyone else the actions are
              shown, disabled, with the reason (the server refuses them too). */}
          <Tooltip title={notMine ? `Not your turn — ${data.cannot_act_reason}` : ''}>
            <span><Button size="small" type="primary" disabled={notMine} onClick={() => setDialog({ action: 'APPROVE', step: openStep })}>Approve</Button></span>
          </Tooltip>
          <Tooltip title={notMine ? `Not your turn — ${data.cannot_act_reason}` : ''}>
            <span><Button size="small" disabled={notMine} onClick={() => setDialog({ action: 'RETURN', step: openStep })}>Return for rework</Button></span>
          </Tooltip>
          <Tooltip title={notMine ? `Not your turn — ${data.cannot_act_reason}` : ''}>
            <span><Button size="small" danger disabled={notMine} onClick={() => setDialog({ action: 'REJECT', step: openStep })}>Reject</Button></span>
          </Tooltip>
        </>
      )}
    </>
  );

  return (
    <div>
      {actionsOnly ? (
        /* 2026-10-07 · the approval queue's cards say the status and draw the chain themselves;
           they need only the actions (and the signing dialog below). */
        <Space wrap align="center" size={8}>{actions}</Space>
      ) : compact ? (
        <Space wrap align="center" size={8}>
          {statusLine}
          {actions}
        </Space>
      ) : (
        <>
          <Space wrap align="center" style={{ marginBottom: 8 }}>{statusLine}</Space>
          {data.steps?.length > 0 && (
            <Steps size="small" direction="vertical" style={{ marginBottom: 12 }}
              items={data.steps.map((s) => ({
                title: s.stage || s.role,
                status: STEP_STATUS[s.status] || 'wait',
                description: (
                  <span style={{ fontSize: 12 }}>
                    {s.role}{s.acted_by ? ` · ${s.acted_by}` : ''}{s.acted_at ? ` · ${new Date(s.acted_at).toLocaleString()}` : ''}
                    {s.esign_id ? ' · e-signed' : s.esign_required ? ' · e-sign required' : ''}
                    {s.sop_reference ? <> · <a href={`#/sop/${encodeURIComponent(s.sop_reference)}`}>{s.sop_reference}</a></> : null}
                    {s.comment ? <><br /><i>{s.comment}</i></> : null}
                  </span>
                ),
              }))} />
          )}
          <Space wrap>{actions}</Space>
        </>
      )}
      {!data.workflow_configured && !actionsOnly && (
        <Alert type="info" showIcon message="No approval workflow is configured for this record type / product class" style={{ marginTop: 8 }} />
      )}
      {!compact && !actionsOnly && data.history?.length > 0 && (
        <Timeline style={{ marginTop: 16 }} items={data.history.map((h) => ({
          color: h.action === 'Approved' ? 'green' : h.action === 'Rejected' ? 'red' : h.action === 'Returned' ? 'orange' : 'gray',
          children: <span style={{ fontSize: 12 }}><b>{h.action}</b> · stage {h.stage} {h.role} · {h.actor || '—'} · {new Date(h.at).toLocaleString()}{h.comment ? ` — ${h.comment}` : ''}</span>,
        }))} />
      )}
      {dialog && dialog.step?.esign_required ? (
        <DualESignatureAuth
          visible
          onCancel={() => setDialog(null)}
          onSubmit={(v) => act({ comment: v.comment || '', signature_id: v.signature_data?.signature_id })}
          signatureType="APPROVAL"
          signatureMeaning={dialog.action === 'APPROVE' ? 'Approved' : dialog.action === 'REJECT' ? 'Rejected' : 'Returned for rework'}
          documentType={objectType}
          documentId={String(objectId)}
          documentNumber={`${objectType}-${objectId}`}
          config={{ enableBasic: true, enableCFR: true, defaultMode: 'cfr', requireComment: true,
                    title: `${dialog.action === 'APPROVE' ? 'Approve' : dialog.action === 'REJECT' ? 'Reject' : 'Return for rework'} — ${dialog.step.stage || dialog.step.role || ''}` }}
        />
      ) : null}
      <Modal open={!!dialog && !dialog.step?.esign_required} onCancel={() => setDialog(null)} onOk={() => act()} confirmLoading={busy}
        title={dialog ? `${dialog.action === 'APPROVE' ? 'Approve' : dialog.action === 'REJECT' ? 'Reject' : 'Return for rework'} — ${dialog.step.stage || dialog.step.role}` : ''}
        okText={dialog?.action === 'APPROVE' ? 'Approve' : 'Confirm'}>
        {/* A stage without e-signature: a decision and its comment. E-signed stages sign in
            DualESignatureAuth above — this dialog keeps no password box of its own. */}
        <Input.TextArea rows={3} placeholder={dialog?.action === 'APPROVE' ? 'Comment (optional)' : 'Reason (mandatory)'} value={comment} onChange={(e) => setComment(e.target.value)} style={{ marginBottom: 8 }} />
      </Modal>
    </div>
  );
}
